import Cross from '../../assets/cross';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
    Button, Drawer, message, Radio,
} from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import Dragger from 'antd/lib/upload/Dragger';
import * as React from 'react';
// import withStyles from 'react-jss';
import * as XLSX from 'xlsx';
import Helper from '../../library/Helper';
import {
    createBulk,
    createBulkInternational,
    downloadSample,
    createMultipleCnWithPx,
} from '../../network/consignments.api';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import { bulkConsignmentStyles } from './bulk-consignments.styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ReduxStore } from 'reducers/redux.types';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { getCustomerCode, getEmployeeStorage, isSuperUserEmployee } from 'auth/local-storage';
import { Master } from 'types/master-data-types';

interface BulkConsignmentProps extends StylesProps<ReturnType<typeof bulkConsignmentStyles>> {
    onClose: () => void;
    config: {
        name: string;
        uploadName: string;
    };
    allowInternational: any;
    showServiceTypeOption?: boolean;
    itemArrayInDomestic: any;
    // allowPieces: boolean;
    uiTheme: ThemeType;
    source: string;
    customFieldsList: any;
    softdataCreationUnifiedFlow: boolean;
    master: Master;
}

const BulkConsignment = (props: BulkConsignmentProps) => {
    const {
        classes,
        onClose,
        config,
        allowInternational,
        showServiceTypeOption,
        itemArrayInDomestic,
        // allowPieces,
        uiTheme,
        source,
        customFieldsList,
        softdataCreationUnifiedFlow,
        master,
    } = props;

    const { t } = useTranslation();

    const [consignmentArray, setConsignments] = React.useState<any>([]);
    const [piecesArray, setPieces] = React.useState<any>([]);
    const [itemsArray, setItems] = React.useState<any>([]);
    const [creating, setCreating] = React.useState<boolean>(false);
    const CustomerUserDetails = master?.CustomerUserDetails;
    const [consignmentCategory, setConsignmentCategory] = React.useState('Domestic');
    const [shipmentType, setShipmentType] = React.useState('Commercial');
    const [rawExcelData, setRawExcelData] = React.useState<any>(null);

    const handleCreate = async () => {
        setCreating(true);
        let response;
        if (!softdataCreationUnifiedFlow) {
            if (consignmentCategory === 'International') {
                response = await createBulkInternational({
                    consignmentArray,
                    piecesArray,
                    itemsArray,
                    isBookedUsingV2: true,
                    isPluginOrder: source === 'plugin' ? true : undefined,
                });
            } else {
                response = await createBulk({
                    consignmentArray,
                    piecesArray,
                    itemsArray: itemArrayInDomestic ? itemsArray : undefined,
                    isBookedUsingV2: true,
                    isPluginOrder: source === 'plugin' ? true : undefined,
                });
            }
        } else {
            // Use raw Excel data or fully transformed consignments
            const payload = rawExcelData;

            // Add extra_details for unified flow
            const isSuperUser = isSuperUserEmployee();
            const employee = getEmployeeStorage();
            const customerCode = getCustomerCode();
            const is_customer_user = CustomerUserDetails?.is_customer_user;

            let created_by;
            if (isSuperUser) {
                created_by = `${employee.employeeCode} (Employee)`;
            } else if (is_customer_user) {
                created_by = `${CustomerUserDetails.username} (Customer User)`;
            } else {
                created_by = `${customerCode} (Customer)`;
            }

            payload.extra_details = {
                creator_code: customerCode,
                created_by,
                customer_user_id: window.localStorage.getItem('customerUserId'),
            };

            response = await createMultipleCnWithPx(payload);
        }
        if (response.isSuccess) {
            message.success(t('excel_upload_success'));
            onClose();
        } else {
            message.error(response.errorMessage);
        }
        setCreating(false);
    };

    const renderButton = () => {
        const hasData = softdataCreationUnifiedFlow
            ? (rawExcelData?.consignment?.length > 0) || consignmentArray.length > 0
            : consignmentArray.length > 0;

        let btnClass = classes.newButtonDisabled;
        if (hasData) {
            btnClass = classes.newButton;
        }

        return (
            <Button
                type="primary"
                loading={creating}
                className={btnClass}
                disabled={!hasData}
                style={{
                    border: `1px solid ${!consignmentArray?.length ? '#EDEDED'
                        : uiTheme.primaryColor}`,
                }}
                onClick={handleCreate}
            >
                {t('submit')}
            </Button>
        );
    };

    const renderHeader = () => {
        return (
            <div className={classes.header}>
                <div className={classes.addText}>
                    <Cross onClick={() => onClose()} alt="close" className={classes.closeIcon} />
                    <span>
                        {config.name}
                    </span>
                </div>
                {renderButton()}
            </div>
        );
    };

    const mapping: Record<string, string> = {
        Unique_Id: 'uniqueId',
        'Client Code': 'clientCode',
        'Consignment Number': 'referenceNumber',
        'Service Type': 'serviceType',
        'Courier Type': 'courierType',
        'Customer Reference Number': 'customerReferenceNumber',
        'Declared Price': 'declaredPrice',
        'Declared Price (non-document)': 'declaredPrice',
        'Risk Surcharge (YES/NO) (non-document)': 'isRiskSurchargeApplicable',
        'Weight(KG) (non-document)': 'weight',
        'Length(cm) (non-document)': 'dimensionslength',
        'Width(cm) (non-document)': 'dimensionswidth',
        'Height(cm) (non-document)': 'dimensionsheight',
        'Consignor Name': 'srcAddressname',
        'Origin Address Line 1': 'srcAddressaddressLine1',
        'Origin Address Line 2': 'srcAddressaddressLine2',
        'Origin City': 'srcAddresscityName',
        'Origin State': 'srcAddressstateName',
        'Origin Pincode': 'srcAddresspincode',
        'Origin Latitude': 'srcAddresslatitude',
        'Origin Longitude': 'srcAddresslongitude',
        'Origin Email': 'srcAddressemail',
        'Destination Pincode': 'dstAddresspincode',
        'Consignee Name': 'dstAddressname',
        'Consignee Phone': 'dstAddressphone',
        'Consignor Phone': 'srcAddressphone',
        'Destination Address Line 1': 'dstAddressaddressLine1',
        'Destination Address Line 2': 'dstAddressaddressLine2',
        'Destination City': 'dstAddresscityName',
        'Destination State': 'dstAddressstateName',
        'Destination Email': 'dstAddressemail',
        'Product Code': 'productCode',
        'Eway Bill': 'ewayBill',
        'Content Type': 'commodityId',
        'Consignment Type': 'consignmentType',
        'In Favor Of': 'codFavorOf',
        'Cod Mode': 'codCollectionMode',
        'Cod Amount': 'codAmount',
        Description: 'description',
        'Origin Country': 'srcAddresscountryName',
        'Destination Country': 'dstAddresscountryName',
        'Destination Latitude': 'dstAddresslatitude',
        'Destination Longitude': 'dstAddresslongitude',
        'Consignee Id': 'consigneeId',
        'Rescheduled Starttime': 'rescheduledStarttime',
        'Rescheduled Date': 'rescheduledDate',
        'Rescheduled Endtime': 'rescheduledEndtime',
        'Origin Company Name': 'srcCompanyName',
        'Consignee EmailId': 'consigneeEmail',
        'Dst Company Name': 'dstCompanyName',
        Notes: 'notes',
        'Number of packages': 'noOfPackages',
        'Inco Terms': 'incoTerms',
        'Shipment Purpose': 'shipmentPurpose',
        'Manifest Number': 'manifestNumber',
        Currency: 'currency',
        'Declared Currency': 'declaredCurrency',
        'Movement Type': 'movementType',
        'Pickup Start Time (HH:MM)': 'pickupTimeSlotStart',
        'Pickup Service Time (Mins)': 'pickupServiceTime',
        'Pickup End Time (HH:MM)': 'pickupTimeSlotEnd',
        'Delivery Start Time (HH:MM)': 'deliveryTimeSlotStart',
        'Delivery End Time (HH:MM)': 'deliveryTimeSlotEnd',
        'Delivery Service Time (Mins)': 'deliveryServiceTime',
        'Pieces Detail': 'piecesDetail',
        'Number of Pieces (non-document)': 'numberOfPieces',
        'Invoice Number': 'invoiceNumber',
        'Destination Address Line 3': 'dstAddressaddressLine3',
        'Destination Address Line 4': 'dstAddressaddressLine4',
        'CPR Number': 'cprNumber',
        'SIM Number': 'simNumber',
        'Device 1': 'device1',
        'Device 2': 'device2',
        'Device 3': 'device3',
        'IMEI 1': 'imei1',
        'IMEI 2': 'imei2',
        'IMEI 3': 'imei3',
        'ALT DEL 1 Name': 'alternateDelAddress1name',
        'ALT DEL 1 Address Line 1': 'alternateDelAddress1addressLine1',
        'ALT DEL 1 Address Line 2': 'alternateDelAddress1addressLine2',
        'ALT DEL 1 Address Line 3': 'alternateDelAddress1addressLine3',
        'ALT DEL 1 Address Line 4': 'alternateDelAddress1addressLine4',
        'ALT DEL 1 Pincode': 'alternateDelAddress1pincode',
        'ALT DEL 1 Phone': 'alternateDelAddress1phone',
        'ALT DEL 1 Alternate Phone': 'alternateDelAddress1alternatePhone',
        'ALT DEL 1 Alternate Phone 2': 'alternateDelAddress1alternatePhone2',
        'ALT DEL 1 City': 'alternateDelAddress1cityName',
        'ALT DEL 1 State': 'alternateDelAddress1stateName',
        'ALT DEL 1 Country': 'alternateDelAddress1countryName',
        'ALT DEL 2 Address Line 1': 'alternateDelAddress2addressLine1',
        'ALT DEL 2 Address Line 2': 'alternateDelAddress2addressLine2',
        'ALT DEL 2 Address Line 3': 'alternateDelAddress2addressLine3',
        'ALT DEL 2 Address Line 4': 'alternateDelAddress2addressLine4',
        'ALT DEL 2 Name': 'alternateDelAddress2name',
        'ALT DEL 2 Pincode': 'alternateDelAddress2pincode',
        'ALT DEL 2 Phone': 'alternateDelAddress2phone',
        'ALT DEL 2 Alternate Phone': 'alternateDelAddress2alternatePhone',
        'ALT DEL 2 Alternate Phone 2': 'alternateDelAddress2alternatePhone2',
        'ALT DEL 2 City': 'alternateDelAddress2cityName',
        'ALT DEL 2 State': 'alternateDelAddress2stateName',
        'ALT DEL 2 Country': 'alternateDelAddress2countryName',
        'REDIRECT Name': 'redirectionAddressname',
        'REDIRECT Address Line 1': 'redirectionAddressaddressLine1',
        'REDIRECT Address Line 2': 'redirectionAddressaddressLine2',
        'REDIRECT Address Line 3': 'redirectionAddressaddressLine4',
        'REDIRECT Address Line 4': 'redirectionAddressaddressLine4',
        'REDIRECT Pincode': 'redirectionAddresspincode',
        'REDIRECT Phone': 'redirectionAddressphone',
        'REDIRECT Alternate Phone': 'redirectionAddressphone',
        'REDIRECT City': 'redirectionAddresscityName',
        'REDIRECT State': 'redirectionAddressstateName',
        'REDIRECT Country': 'redirectionAddresscountryName',
        'Consignor Alternate Phone': 'srcAddressalternatePhone',
        'Consignor Alternate Phone 2': 'srcAddressalternatePhone2',
        'Dimension Unit': 'dimensionUnit',
        'Weight Unit': 'weightUnit',
        'Invoice Date': 'invoiceDate',
        'Export Invoice Date': 'exportInvoiceDate',
        'Consignor Kyc Doc Type': 'consignorKycDocType',
        'Consignor Kyc Doc Number': 'consignorKycDocNumber',
        'Consignor Kyc Front Image': 'consignorKycFrontImage',
        'Consignor Kyc Back Image': 'consignorKycBackImage',
        'Invoice Type': 'invoiceType',
        'Is Battery': 'isBattery',
        'Consignor Company Name': 'consignorCompanyName',
        'Consignee Company Name': 'consigneeCompanyName',
        'Sender Type': 'originType',
        'consignor IEC Number': 'consignorIECNumber',
        'consignor PAN Id': 'consignorPanId',
        'consignor Tax Id': 'consignorTaxId',
        'Destination Type': 'destinationType',
        'NEFI flag': 'nefiFlag',
        'Customer Seller Code': 'custSellerCode',
        'Consignor GSTIN Number': 'consignorGSTINNumber',
        'Cess Value': 'cessValue',
        'Sender CPC Code': 'cpcCode',
        'Sender Bank Account No.': 'senderBankAccountNumber',
        'Sender Bank AD Code': 'senderBankADCode',
        'Sender Bank IFSC Code': 'senderBankIFSCCode',
        'Billing Address Pincode': 'billAddresspincode',
        'Biller Name': 'billAddressname',
        'Biller Phone': 'billAddressphone',
        'Billing Address Line 1': 'billAddressaddressLine1',
        'Billing Address Line 2': 'billAddressaddressLine2',
        'Billing Address City': 'billAddresscityName',
        'Billing Address State': 'billAddressstateName',
        'Billing Address Country': 'billAddresscountryName',
        'Billing Address Email': 'billAddressemail',
        'Biller Alternate Phone': 'billAddressalternatePhone',
        'Biller Alternate Phone 2': 'billAddressalternatePhone2',
        'Delivery Instructions': 'deliveryInstructions',
        'Against Bond Lut': 'againstBondLut',
        'ECom Shipment': 'ecomShipment',
        MEIS: 'meis',
        'IORI Number': 'ioriNumber',
        'EORI Number': 'eoriNumber',
        'Freight Cost': 'freightCost',
        'Freight Cost Currency': 'freightCostCurrency',
        'FOB Value': 'fobValue',
        'FOB Value Currency': 'fobValueCurrency',
        'Total GST Paid Amount': 'totalGSTPaidAmount',
        'Total GST Paid Currency': 'totalGSTPaidCurrency',
        'EOR Details': 'eorDetails',
        'IOR Details': 'iorDetails',
        'Receiver VAT Number': 'receiverVATNumber',
        'Pickup Hub Code': 'pickupHubCode',
        'Shipment Terms': 'deliveryTerms',
        'Type Of Delivery': 'typeOfDelivery',
        'Retail Transaction': 'isRetailTransaction',
        'Cash On Pickup Amount': 'copAmount',
        'Origin Address Email': 'srcAddressemail',
        'Destination Address Email': 'dstAddressemail',
        'Origin W3W Code': 'srcAddressw3wCode',
        'Destination W3W Code': 'dstAddressw3wCode',
        'EWB Date': 'ewbDate',
        'EWB Number': 'ewbNumber',
        'Origin Address Hub Code': 'srcAddressaddressHubCode',
        'Destination Address Hub Code': 'dstAddressaddressHubCode',
        'Return Address Hub Code': 'retAddressaddressHubCode',
        'Risk Surcharge Type': 'riskSurchargeType',
        'Consignee Alternate Phone': 'dstAddressalternatePhone',
        'Consignee Alternate Phone 2': 'dstAddressalternatePhone2',
        'SRF Number': 'srfNumber',
        'Pickup Date (MM/DD/YYYY)': 'pickupDate',
        'Origin Address Code': 'srcAddressaddressCode',
        'Destination Address Code': 'dstAddressaddressCode',
        'Return Address Code': 'retAddressaddressCode',
        'Return Name': 'retAddressname',
        'Return Phone': 'retAddressphone',
        'Return Address Line 1': 'retAddressaddressLine1',
        'Return Address Line 2': 'retAddressaddressLine2',
        'Return City': 'retAddresscityName',
        'Return State': 'retAddressstateName',
        'Return Country': 'retAddresscountryName',
        'Return Pincode': 'retAddresspincode',
        'Return Latitude': 'retAddresslatitude',
        'Return Longitude': 'retAddresslongitude',
        'Return Email': 'retAddressemail',
        'Return Company Name': 'retAddresscompanyName',
        'RTO Name': 'retAddressname',
        'RTO Phone': 'retAddressphone',
        'RTO Address Line 1': 'retAddressaddressLine1',
        'RTO Address Line 2': 'retAddressaddressLine2',
        'RTO City': 'retAddresscityName',
        'RTO State': 'retAddressstateName',
        'RTO Country': 'retAddresscountryName',
        'RTO Pincode': 'retAddresspincode',
        'Line Meters': 'lineMeters',
        'Sender Partner Id': 'senderPartnerId',
        'Receiver Partner Id': 'receiverPartnerId',
        'High Value Id Number': 'highValueDetailsidNum',
        'High Value Name': 'highValueDetailsname',
        'High Value Id Expiration': 'highValueDetailsidExpiry',
        'High Value Id Place Of Issue': 'highValueDetailsissuePlace',
        'High Value Nationality': 'highValueDetailsnationality',
        'High Value Mobile No': 'highValueDetailsphone',
        'High Value Amount': 'highValueDetailsamount',
        'High Value Billed To Merchant': 'highValueDetailsbilledToMerchant',
        'HS Code': 'highValueDetailshsCode',
        'HSN Code': 'hsnCode',
        'Customer Civil Id': 'customerCivilId',
        'Receiver Civil Id': 'receiverCivilId',
        'Fragile content': 'isFragile',
        'Commodity Name': 'commodityName',
        'Source address alternate phone': 'srcAddressalternatePhone',
        'Source address alternate phone 2': 'srcAddressalternatePhone2',
        'Destination address alternate phone': 'dstAddressalternatePhone',
        'Destination address alternate phone 2': 'dstAddressalternatePhone2',
        'IMEI-1': 'imei1',
        'IMEI-2': 'imei2',
        'IMEI-3': 'imei3',
        'Sim Number': 'simNumber',
        Quantity: 'quantity',
        'Service Item': 'serviceItem',
        'Service Item Dimension Unit': 'serviceItemDimensionUnit',
        'Item Quantity': 'itemQuantity',
        'Item Unit': 'itemUnit',
        'Is International': 'isInternational',
        'GSTIN Shipper': 'senderGstin',
        'GSTIN Receiver': 'receiverGstin',
        'Invoice Link': 'invoiceLink',
        'Movement Classification': 'movementClassification',
        'Pudo Store': 'kiranaHubCode',
        'National Destination Code': 'dstAddressnationalDestinationCode',
        'Origin National Address Code': 'srcAddressnationalAddressCode',
        'Destination National Address Code': 'dstAddressnationalAddressCode',
        'Return National Address Code': 'retAddressnationalAddressCode',
        'Package Type': 'packagingType',
        'PUDO Cod Amount': 'pudoCodAmount',
        'AK Number': 'akNumber',
        'Destination Location Id': 'dstAddresslocationId',
        'Destination Location Type': 'dstAddresslocationType',
        'Origin Location Id': 'srcAddresslocationId',
        'Origin Location Type': 'srcAddresslocationType',
        'Collect Import Vat': 'collect_import_vat',
        'ASN Number': 'asnNumber',
        'Origin Location Code': 'originLocationCode',
        'Num Packages': 'num_packages',
        'Mode of pickup': 'modeOfPickup',
        'Prepaid Amount': 'prepaidAmount',
        'Group Code': 'groupCode',
        'Customs Amount': 'customsAmount',
        'Customs Currency': 'customsCurrency',
        'Import Reference Number': 'importReferenceNumber',
        'Export Reference Number': 'exportReferenceNumber',
        'Is Customs Declarable': 'isCustomsDeclarable',
        'Consignee Code': 'consigneeCode',
        'Invoice Amount': 'invoiceAmount',
        Purpose: 'purpose',
        Segment: 'segment',
        'Reference Image List': 'referenceImageList',
        'Is Csbv': 'isCsbv',
        'Manufacturing Country': 'manufacturingCountry',
        'Total Tax': 'totalTax',
        'Tax Percentage': 'taxPercentage',
        'Is Dangerous Good': 'isDangerousGood',
        'Dangerous Good Accessible': 'dangerousGoodAccessible',
        'Dangerous Good UnNumber': 'dangerousGoodUnNumber',
        'Dangerous Good Class': 'dangerousGoodClass',
        'Dangerous Good Name': 'dangerousGoodName',
        'Dangerous Good Division': 'dangerousGoodDivision',
        'Dangerous Good Packing Group': 'dangerousGoodPackingGroup',
        'Dangerous Good Category': 'dangerousGoodCategory',
        'Dangerous Good Category Type': 'dangerousGoodCategoryType',
        'Dangerous Good Category Packing Instruction': 'dangerousGoodCategoryPackingInstruction',
        'Stackable (YES/NO)': 'isStackable',
        IGST: 'igst',
        CGST: 'cgst',
        SGST: 'sgst',
        'Reciever Tax GSTIN': 'taxDetailsSenderGstin',
        'Sender Tax GSTIN': 'taxDetailsRecieverGstin',
        'Forward Reference Number': 'forwardReferenceNumber',
        'Purchase Order Number': 'purchaseOrderNumber',
        'Delivery Date (MM/DD/YYYY)': 'deliveryDate',
    };

    const pieceDetailHeaderMapping: Record<string, string> = {
        'Customer Reference Number': 'customerReferenceNumber',
        Unique_Id: 'uniqueId',
        Description: 'description',
        Quantity: 'quantity',
        Length: 'length',
        Width: 'width',
        Height: 'height',
        Weight: 'weight',
        'Dimension Unit': 'dimensionUnit',
        'Weight Unit': 'weightUnit',
        Volume: 'volume',
        'Volume Unit': 'volumeUnit',
        'Model Code': 'modelCode',
        'Item Category': 'itemCategory',
        'Item Size': 'itemSize',
        'Piece Reference Number': 'pieceReferenceNumber',
        'Reference Number': 'pieceReferenceNumber',
        'Piece Number': 'pieceNumber',
        'Number of Items': 'numberOfItems',
        'Declared Value': 'declaredValue',
        'Volumetric Weight': 'volumetricWeight',
        'Piece Unique ID': 'pieceUniqueId',
        'Item Unit': 'itemUnit',
        'Piece Product Code': 'pieceProductCode',
        'Cod Amount': 'codAmount',
        'Net Weight': 'netWeight',
        'HSN Code': 'hsnCode',
        'HS Code': 'hsnCode',
        'Reference Image List': 'referenceImageList',
        'Manufacturing Country': 'manufacturingCountry',
        'Total Tax': 'totalTax',
        'Tax Percentage': 'taxPercentage',
        'MEIS Applicable': 'meisApplicable',
        'Is Dangerous Good': 'isDangerousGood',
        'Dangerous Good Accessible': 'dangerousGoodAccessible',
        'Dangerous Good UnNumber': 'dangerousGoodUnNumber',
        'Dangerous Good Class': 'dangerousGoodClass',
        'Dangerous Good Name': 'dangerousGoodName',
        'Dangerous Good Division': 'dangerousGoodDivision',
        'Dangerous Good Packing Group': 'dangerousGoodPackingGroup',
        'Dangerous Good Category': 'dangerousGoodCategory',
        'Dangerous Good Category Type': 'dangerousGoodCategoryType',
        'Dangerous Good Category Packing Instruction': 'dangerousGoodCategoryPackingInstruction',
        'PO Number': 'purchaseOrderNumber',
        ECCN: 'eccn',
        'KIT (YES/NO)': 'isKit',
        IGST: 'igst',
        CGST: 'cgst',
        SGST: 'sgst',
        'Reciever Tax GSTIN': 'taxDetailsSenderGstin',
        'Sender Tax GSTIN': 'taxDetailsRecieverGstin',
        'Forward Reference Number': 'forwardReferenceNumber',
        'Product Family': 'productFamily',
        'Product Category': 'productCategory',
        'Brand Name': 'brandName',
    };

    const itemDetailHeaderMapping: Record<string, string> = {
        'Customer Reference Number': 'customerReferenceNumber',
        'Piece Number': 'pieceNumber',
        Quantity: 'numberOfItems',
        'Item Rate': 'itemValue',
        'Item Description': 'itemDescription',
        'SKU Number': 'skuNumber',
        'HSN Code': 'hsnCode',
        'Item Total Value': 'dutyValue',
        'Product Dead Weight': 'productDeadWeight',
        'Product Type': 'productType',
        'Product Url': 'productUrl',
        'Origin Of Goods': 'originOfGoods',
        'Gst Value': 'gstValue',
        'Gst Percentage': 'gstPercentage',
        'Cess Value': 'cessValue',
        'ATE Number': 'ateNumber',
        Composition: 'composition',
        'Rodtep Scheme': 'rodtepScheme',
        'Product Height': 'productHeight',
        'Product Length': 'productLength',
        'Product Width': 'productWidth',
        'Cod Amount': 'codAmount',
        'Piece Unique ID': 'pieceUniqueId',
        'Item Number': 'itemNumber',
        'HS Code': 'hsCode',
        'Manufacturing Country': 'manufacturingCountry',
        'Cost Per Piece': 'costPerPiece',
        'Total Tax': 'totalTax',
        'Tax Percentage': 'taxPercentage',
    };

    const invoiceDetailHeaderMapping: Record<string, string> = {
        'Unique Id': 'unique_id',
        'Invoice Number': 'invoice_number',
        'Invoice Date': 'invoice_date',
        'Invoice Amount': 'invoice_amount',
        'Invoice Status': 'invoice_status',
        'Piece Reference Number': 'piece_reference_number',
        'EWB Number': 'ewb_number',
        'Sender GSTIN': 'sender_gstin',
        'Receiver GSTIN': 'receiver_gstin',
        'EWB Creation Time': 'ewb_creation_time',
        'EWB Expiry Time': 'ewb_expiry_time',
        'Transporter GSTIN': 'transporter_gstin',
        'Transporter Name': 'transporter_name',
    };

    const DateKeys: string[] = [
        'exportInvoiceDate',
        'invoiceDate',
        'ewbDate',
        'pickupDate',
        'deliveryDate',
    ];

    const TimeKeys: string[] = [
        'pickupTimeSlotStart',
        'pickupTimeSlotEnd',
        'deliveryTimeSlotStart',
        'deliveryTimeSlotEnd',
    ];

    const arrayColumnsList: string[] = [
        'referenceImageList',
    ];

    const is_customer_user = CustomerUserDetails?.is_customer_user;

    const invoiceDateTimeKeys: string[] = [
        'invoice_date',
        'ewb_creation_time',
        'ewb_expiry_time',
    ];

    const getConsignmentArray = (data: any[]) => {
        const headers = data[0];
        const slicedData = data.slice(1);
        const isSuperUser = isSuperUserEmployee();
        const employee = getEmployeeStorage();
        const customerCode = getCustomerCode();
        const customFieldsMapping = (customFieldsList || [])?.reduce((acc: any, item: any) => {
            acc[item?.prettyName] = item?.columnId;
            return acc;
        }, {});
        const extendedMapping = { ...mapping, ...customFieldsMapping };
        const newData: any[] = slicedData.map((item) => {
            const returnObj: Record<string, any> = {};
            Object.keys(item).forEach((key: string) => {
                let keyUpdate = headers[key];
                keyUpdate = extendedMapping[keyUpdate];
                if (DateKeys.includes(keyUpdate)) {
                    returnObj[keyUpdate] = moment(item[key]).add(1, 'h').format('YYYY-MM-DD');
                } else if (TimeKeys.includes(keyUpdate)) {
                    returnObj[keyUpdate] = moment(item[key], 'HH:mm').format('HH:mm');
                } else if (arrayColumnsList.includes(keyUpdate)) {
                    returnObj[keyUpdate] = Helper.convertExcelTextToArray(item[key]);
                } else if (customFieldsList?.some((field: any) => field.columnId === keyUpdate
                    && field.dataType === 'datetime')) {
                    returnObj[keyUpdate] = item[key]
                        ? Helper.getEpoch(item[key])
                        : null;
                } else {
                    returnObj[keyUpdate] = item[key];
                }
            });

            if ([null, ''].includes(returnObj.courierType)) {
                returnObj.courierType = 'NON-DOCUMENT';
            }
            // eslint-disable-next-line max-len, no-nested-ternary
            returnObj.createdBy = isSuperUser ? `${employee.employeeCode} (Employee)` : (is_customer_user ? `${CustomerUserDetails.username} (Customer User)` : `${customerCode} (Customer)`);
            return returnObj;
        });
        return newData;
    };

    const getPiecesArray = (data: any[]) => {
        const headers = data[0];
        const slicedData = data.slice(1);
        const newData: any[] = slicedData.map((item) => {
            const returnObj: Record<string, any> = {};
            Object.keys(item).forEach((key: string) => {
                let keyUpdate = headers[key];
                keyUpdate = pieceDetailHeaderMapping[keyUpdate];
                if (arrayColumnsList.includes(keyUpdate)) {
                    returnObj[keyUpdate] = Helper.convertExcelTextToArray(item[key]);
                } else if (keyUpdate) {
                    returnObj[keyUpdate] = item[key];
                }
            });
            return returnObj;
        });
        return newData.filter((obj: any) => (Object.keys(obj).length) > 0);
    };

    const getItemsArray = (data: any[]) => {
        const headers = data[0];
        const slicedData = data.slice(1);
        const itemDetailsMapping = consignmentCategory === 'Domestic'
            ? {
                ...itemDetailHeaderMapping,
                Quantity: 'quantity',
            }
            : itemDetailHeaderMapping;
        const newData: any[] = slicedData.map((item) => {
            const returnObj: Record<string, any> = {};
            Object.keys(item).forEach((key: string) => {
                let keyUpdate = headers[key];
                keyUpdate = itemDetailsMapping[keyUpdate];
                if (keyUpdate) {
                    returnObj[keyUpdate] = item[key];
                }
            });
            return returnObj;
        });
        return newData.filter((obj: any) => (Object.keys(obj).length) > 0);
    };

    const getInvoicesArray = (data: any[]) => {
        if (!data || data.length === 0) return [];
        const headers = data[0];
        const slicedData = data.slice(1);
        const newData: any[] = slicedData.map((item) => {
            const returnObj: Record<string, any> = {};
            Object.keys(item).forEach((key: string) => {
                let keyUpdate = headers[key];
                keyUpdate = invoiceDetailHeaderMapping[keyUpdate];
                if (invoiceDateTimeKeys.includes(keyUpdate) && item[key] && moment(item[key]).isValid()) {
                    returnObj[keyUpdate] = item[key] ? Helper.getEpoch(item[key]) : null;
                } else {
                    returnObj[keyUpdate] = item[key];
                }
            });
            return returnObj;
        });
        return newData.filter((obj: any) => (Object.keys(obj).length) > 0);
    };

    const transformData = (data: any) => {
        const newData: any = {};
        newData.consignmentArray = getConsignmentArray(data?.consignment || []) || [];
        if (data?.pieces) {
            newData.piecesArray = getPiecesArray(data?.pieces || []) || [];
            if (data?.items) {
                newData.itemsArray = getItemsArray(data?.items || []) || [];
            }
        }
        if (data?.invoices) {
            newData.invoicesArray = getInvoicesArray(data?.invoices) || [];
        }
        return newData;
    };

    const findKeyInObject = (obj: any, searchKey: string): string | null => {
        const keys = Object.keys(obj);
        const foundKey = keys.find((key) => (
            key === searchKey
        ));
        return foundKey || null;
    };

    const validateExcelData = (data: any): { isValid: boolean; error?: string } => {
        // 1. Check if consignment sheet exists and has data
        if (!data?.consignment?.length) {
            return { isValid: false, error: 'Consignment sheet is empty or missing' };
        }

        // Find Unique_Id key in first consignment object
        const uniqueIdKey = findKeyInObject(data.consignment[0], 'Unique_Id');

        if (!uniqueIdKey) {
            return { isValid: false, error: 'Unique_Id column not found in consignment sheet' };
        }

        // Check if all consignments have Unique_Id (allow 0 as valid value)
        const consignmentsWithoutId = data.consignment.filter((row: any) => {
            const value = row[uniqueIdKey];
            return value === null || value === undefined || value === '';
        });

        if (consignmentsWithoutId.length) {
            return {
                isValid: false,
                error: `${consignmentsWithoutId.length} consignment(s) missing Unique_Id.`,
            };
        }

        if (data?.pieces?.length) {
            const pieceUniqueIdKey = findKeyInObject(data.pieces[0], 'Unique_Id');

            if (!pieceUniqueIdKey) {
                return {
                    isValid: false,
                    error: 'Pieces sheet missing "Unique_Id" column.',
                };
            }

            const piecesWithoutId = data.pieces.filter((row: any) => {
                const value = row[pieceUniqueIdKey];
                return value === null || value === undefined || value === '';
            });

            if (piecesWithoutId.length) {
                return {
                    isValid: false,
                    error: `${piecesWithoutId.length} piece(s) missing Unique_Id.`,
                };
            }
        }

        if (data?.items?.length) {
            const pieceUniqueIdKey = findKeyInObject(data.items[0], 'Piece Unique ID');

            if (!pieceUniqueIdKey) {
                return {
                    isValid: false,
                    error: 'Items sheet missing "Piece Unique ID" column.',
                };
            }

            const itemsWithoutId = data.items.filter((row: any) => {
                const value = row[pieceUniqueIdKey];
                return value === null || value === undefined || value === '';
            });

            if (itemsWithoutId.length) {
                return {
                    isValid: false,
                    error: `${itemsWithoutId.length} item(s) missing 'Piece Unique ID'.`,
                };
            }
        }

        if (data?.invoices?.length) {
            const invoiceUniqueIdKey = findKeyInObject(data.invoices[0], 'Unique Id');

            if (!invoiceUniqueIdKey) {
                return {
                    isValid: false,
                    error: 'Invoices sheet missing "Unique Id" column.',
                };
            }

            const invoicesWithoutId = data.invoices.filter((row: any) => {
                const value = row[invoiceUniqueIdKey];
                return value === null || value === undefined || value === '';
            });

            if (invoicesWithoutId.length) {
                return {
                    isValid: false,
                    error: `${invoicesWithoutId.length} invoice(s) missing 'Unique Id'.`,
                };
            }
        }

        return { isValid: true };
    };

    const handleParsedData = async (data: any) => {
        if (!data) return;
        let slicedData;
        if (!slicedData && source !== 'plugin' && softdataCreationUnifiedFlow) {
            const validation = validateExcelData(data);
            if (!validation.isValid) {
                message.error(validation.error);
                return;
            }

            setRawExcelData(data);
        } else {
            slicedData = transformData(data);
            setConsignments(slicedData.consignmentArray);
            setPieces(slicedData.piecesArray);
            setItems(slicedData.itemsArray);
        }
    };

    const onFileRecieved = async (file: any) => {
        // For unified flow, parse Excel directly to get all sheets as objects
        if (softdataCreationUnifiedFlow) {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
                const result: any = {};

                workbook.SheetNames.forEach((sheetName) => {
                    const sheet = workbook.Sheets[sheetName];
                    // Convert to JSON with first row as headers, including empty columns
                    const sheetData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                    // Post-process to handle Date objects from cellDates: true
                    result[sheetName] = sheetData.map((row: any) => {
                        const processedRow: any = {};
                        Object.keys(row).forEach((key) => {
                            const value = row[key];
                            if (value instanceof Date && !Number.isNaN(value.getTime())) {
                                processedRow[key] = moment(value).format('YYYY-MM-DD');
                            } else {
                                processedRow[key] = value;
                            }
                        });
                        return processedRow;
                    });
                });
                handleParsedData(result);
            };
            reader.readAsBinaryString(file);
        } else {
            // Old flow with mapping
            const consignmentArr: any = await Helper.handleXLSXFileAsObject(file, Object.keys(mapping), 0);
            handleParsedData(consignmentArr);
        }
    };

    const handleUploadChange = (info: UploadChangeParam) => {
        const { status } = info.file;
        if (status === 'done') {
            onFileRecieved(info.file.originFileObj);
        }
    };

    const customRequest = ({ onSuccess, file }: any) => {
        setTimeout(() => {
            onSuccess(null, file);
        }, 100);
    };

    const renderUpload = () => {
        const hasData = softdataCreationUnifiedFlow
            ? (rawExcelData?.consignment?.length > 0) || consignmentArray.length > 0
            : consignmentArray.length > 0;

        return (
            <div
                className={classes.uploadBox}
            >
                <Dragger
                    multiple={false}
                    accept=".xlsx, .xls, .csv"
                    name="file"
                    disabled={hasData}
                    onChange={handleUploadChange}
                    customRequest={customRequest}
                >
                    <p className={classes.uploadText}>
                        <UploadOutlined className={classes.closeIcon} />
                        {t('bulk_consignment_upload_text')}
                    </p>
                    <p className={classes.uploadHint}>
                        {t('drag_and_drop_text')}
                        {t('or')}
                        <Button type="link" color={uiTheme.primaryColor}>
                            {t('Browse')}
                        </Button>
                        {t('to choose file')}
                    </p>
                </Dragger>
            </div>
        );
    };

    const handleDownload = async () => {
        let fileBuffer;

        if (consignmentCategory === 'International') {
            fileBuffer = await downloadSample({
                isInternational: true,
                isCommercialShipment: shipmentType === 'Commercial',
            });
        } else {
            fileBuffer = await downloadSample({ isInternational: false });
        }

        const fileName = fileBuffer.filename;
        if (fileBuffer.isSuccess) {
            Helper.downloadFileData(
                fileBuffer.data,
                fileName || 'sample-consignment-upload.xlsx',
                true,
            );
            message.success('Downloaded Successfully');
        } else {
            message.error('Something went wrong while downloading the sample file');
        }
    };

    const renderDownload = () => {
        return (
            <Button
                type="link"
                className={classes.downloadBtn}
                onClick={handleDownload}
            >
                <DownloadOutlined />
                {t('download_sample_file')}
            </Button>
        );
    };

    const renderConsignmentCategory = () => {
        const categoryOptions = [t('domestic'), t('international')];
        const shipmentTypeOptions = [t('commercial'), t('non_commercial')];
        return (
            <>
                <div className={classes.consignmentCategory}>
                    <div className={classes.flexColumn}>
                        {t('consignment_category')}
                        :
                    </div>
                    <div>
                        <Radio.Group
                            options={
                                categoryOptions
                            }
                            value={consignmentCategory}
                            onChange={(e) => setConsignmentCategory(e.target.value)}
                        />
                    </div>
                </div>
                {
                    (showServiceTypeOption && consignmentCategory === 'International'
                        ? (
                            <div className={classes.consignmentCategory}>
                                <div className={classes.flexColumn}>
                                    {t('shipment_type')}
                                    :
                                </div>
                                <div>
                                    <Radio.Group
                                        options={
                                            shipmentTypeOptions
                                        }
                                        value={shipmentType}
                                        onChange={(e) => setShipmentType(e.target.value)}
                                    />
                                </div>
                            </div>
                        )
                        : null
                    )
                }
            </>
        );
    };

    return (
        <Drawer
            visible
            width="35%"
            title={renderHeader()}
            onClose={() => onClose()}
            className={classes.main}
            closable={false}
        >
            {allowInternational ? renderConsignmentCategory() : null}
            {renderUpload()}
            {source === 'plugin' ? null : renderDownload()}
        </Drawer>
    );
};
const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const customFieldsList = master?.config?.complete_custom_fields_list || [];
    const softdataCreationUnifiedFlow = master?.parts_to_show?.softdata_creation_unified_flow_enabled;
    return {
        uiTheme: state.uiTheme,
        customFieldsList,
        softdataCreationUnifiedFlow,
    };
};
const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: bulkConsignmentStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};


export default (GenericHoc(hocConfig)(BulkConsignment));
